import { formatText } from "@eusoft/webapp-ui";
import { context } from "./Context";
import { PaymentCardType, PrintCommandType, PrinterAlignment, PrinterTextStyle, PrintImageFormat, type IPrintCommand } from "../entities/Commands";
import { showWaitForCardAsync } from "../pages/paymentCard/WaitForCard";
import type { Theme } from "./Theme";
import type { KeyType, IPaymentCard, IPaymentService, IReadCard, IWriteCard, IBarcode, CardType, IPrinter } from "../abstraction/IPaymentService";


type RequestId = string;

declare global {

    var appBridge: {
        getStatus(): number;
        readCard(payToken?: string): RequestId;
        readQrCard(payToken?: string): RequestId;
        getDevice(): string;
        signPayload(payload: string, type: KeyType, base64: boolean): string;
        getTrustChain(type: KeyType): string;
        getCertificate(type: KeyType): string;
        getFingerprint(type: KeyType): string;
        requestFcmToken(): RequestId;
        writeCard(uri: string): RequestId;
        openCodeScanner(): RequestId;
        closeCodeScanner(): void;
        authorizeKey(type: KeyType): RequestId;
        setAccessToken(token: string, userId: string): void;
        getVersion(): string;
        ensureDeviceSecure(prompt: boolean): boolean;  
        share(options: string): void;
        print(printerModel:string, commandList: string): boolean;
        listPrinters(): string;
        setTheme(theme: Theme, reboot: boolean);
        bankPosList(): string;
        bankPosConfigure(posName: string, optionsJson: string);
        bankPosConnect(posName: string): string;
        bankPosPay(posName: string, payJson: string): string;
    }
}

interface IBankPosInfo {
    name: string;
}

export interface IBankPosPayResult {
    isSuccess: boolean;
    code: number;
    message?: string;
    transactionCode?: string;
    extra?: Record<string, any>;
}

export interface IBankPosPayment {
    amount: number;
    currency: String;
    description: String;
    orderId: String;
}


interface IDeviceInfo {
    manufacturer: string;
    brand: string;
    model: string;
    device: string;
    product: string;
    hardware: string;
    board: string;
    bootloader: string;
    fingerprint: string;
    serial: string;
    versionSdk: number;
    versionRelease: string;
    versionCodename: string;
    cpuAbi: string;
}
interface INativeMessage<T = unknown> {
    type: string;
    data: T;
    error?: string; 
    requestId: RequestId;
}

interface IAppVersion {
    versionName: string;
    versionCode: number;
    packageName: string;
}

interface IShareOptions {
    title?: string;
    text?: string;
    imageUri?: string;
}



export function isNativeApp() {
    return "appBridge" in window;
}

class NativeApp implements IPaymentService {

    protected async executeAsync<T = string>(type: string, action: () => RequestId, timeoutSec: number) {

        return new Promise<T>((res, rej) => {

            const timer = setTimeout(() => {
                window.removeEventListener("message", handler);
                rej(new Error(formatText("err-timeout") as string));
            }, timeoutSec * 1000);

            let reqId;

            const handler = (ev: MessageEvent) => {

                let obj: INativeMessage;

                if (typeof ev.data == "string") {
                    try {
                        obj = JSON.parse(ev.data) as INativeMessage;
                    }
                    catch {
                        return;
                    }
                }
                else if (typeof ev.data == "object")
                    obj = ev.data;

                console.debug("message", obj);

                if (typeof obj == "object" && "type" in obj) {

                    if (obj.type == type && (!reqId || obj.requestId == reqId)) {

                        window.removeEventListener("message", handler);

                        clearTimeout(timer);

                        if (obj.error)
                            return rej(new Error(obj.error));

                        return res(obj.data as T);
                    }
                }
            }

            window.addEventListener("message", handler);

            reqId = action();

            console.debug("Native request: ", reqId);
        });
    }

    getFcmTokenAsync() {
        return this.executeAsync("fcmToken", () => appBridge.requestFcmToken(), 10);
    }

    getTrustChain(type: KeyType) {
        return JSON.parse(appBridge.getTrustChain(type)) as string[];
    }

    getCertificate(type: KeyType) {
        return appBridge.getCertificate(type);
    }

    signPayloadPos(payload: string) {
        return appBridge.signPayload(payload, "pos", false);  
    }

    getFingerprint(type: KeyType) {
        return appBridge.getFingerprint(type);
    }

    signPayloadAuthAsync(payload: string) {
        return this.executeAsync("signature", () => appBridge.signPayload(payload, "auth", false), 30);
    }

    getDeviceInfo() {
        return JSON.parse(appBridge.getDevice()) as IDeviceInfo;
    }

    readCardAsync(options: IReadCard) {

        const type: CardType = options.type ?? context.options?.paymentType === PaymentCardType.QrCode ? "qr" : "nfc";

        if (type == "nfc") {
            return showWaitForCardAsync({ message: options.message, amount: options.amount },
                () => this.executeAsync<IPaymentCard>("readCard",
                    () => appBridge.readCard(options.payToken), 30));
        }
        else {

            return this.executeAsync<IPaymentCard>("readCard",
                () => appBridge.readQrCard(options.payToken), 30);       
        }
    }

    writeCardAsync(options: IWriteCard) {

        return showWaitForCardAsync({ message: options.message },
            () => this.executeAsync<boolean>("writeCard",
                () => appBridge.writeCard(options.uri), 60));
    }

    authorizeKeyAsync(type: KeyType) {
        return this.executeAsync<boolean>("authorizeKey", () => appBridge.authorizeKey(type), 30);
    }

    setAccessToken(token: string, userId: string) {
        appBridge.setAccessToken(token, userId);
    }

    async scanCodeAsync() {

        try {
            return await this.executeAsync<IBarcode>("barcode", () => appBridge.openCodeScanner(), 60 * 10);
        }
        catch (ex) {
            appBridge.closeCodeScanner();
            throw ex;
        }         
    }

    getVersion() {
        return JSON.parse(appBridge.getVersion()) as IAppVersion;   
    }

    ensureDeviceSecure(prompt: boolean) {

        return appBridge.ensureDeviceSecure(prompt) as boolean;    
    }

    share(options: IShareOptions) {
        return appBridge.share(JSON.stringify(options));
    }
    
    print(printerModel: string, commandList: IPrintCommand[]) {

        const json = JSON.stringify(commandList, (key, value) => {
            if (key == "type")
                return PrintCommandType[value];
            if (key == "alignment")
                return PrinterAlignment[value];
            if (key == "textStyle")
                return PrinterTextStyle[value];
            if (key == "format")
                return PrintImageFormat[value];
            if(value === null ||  value === undefined )
                return;
            return value;
        })

        return appBridge.print(printerModel, json);
    }
    
    listPrinters(): IPrinter[] {
        return JSON.parse(appBridge.listPrinters()) as IPrinter[];
    }

    bankPosList(): IBankPosInfo[] {
        const json = appBridge.bankPosList();
        return JSON.parse(json);
    }

    bankPosConfigureAsync(posName: string, options: object) {
        
        return this.executeAsync<IBankPosPayResult>("bankPosConfigure",
            () => appBridge.bankPosConfigure(posName, JSON.stringify(options)), 120);
    }

    bankPosConnectAsync(posName: string) {

        return this.executeAsync<boolean>("bankPosConnect",
            () => appBridge.bankPosConnect(posName), 120);
    }

    bankPosPayAsync(posName: string, payment: IBankPosPayment) {

        return this.executeAsync<IBankPosPayResult>("bankPosPay",
            () => appBridge.bankPosPay(posName, JSON.stringify(payment)), 120);
    }

}


export const nativeApp = new NativeApp();